import { useState } from 'react';
import {
  createFileRoute,
  useParams,
  useNavigate,
} from '@tanstack/react-router';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import { ProtectedRoute } from '@/components/protected-route';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';

import JobTaskListView from '@/components/tasks/list';
import JobNotesList from '@/components/notes/job-notes-list';

import { selectJobOverviewData, selectTasksByJobId, selectNotesByJobId, selectCheckinByBooking } from '@/store/selectors';
import { addJobState, updateJobState } from '@/store/actions';
import { updateHelpModal, updateNotesModal } from '@/store/slices/appStateSlice';
import { loadMoreNotes } from '@/store/actions';
import type { RootState } from '@/store';

export const Route = createFileRoute('/job/$jobId/$date/tasks')({
  component: () => (
    <ProtectedRoute>
      <JobTasksPage />
    </ProtectedRoute>
  ),
});


function JobTasksPage() {
  const { jobId, date } = useParams({ from: '/job/$jobId/$date/tasks' });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [currentPage, setCurrentPage] = useState(1);

  // Use Redux selector to get all job-related data
  const { booking, job, jobState } = useSelector((state: RootState) =>
    selectJobOverviewData(state, jobId)
  );

  const checkinToday = useSelector((state: RootState) =>
    booking ? selectCheckinByBooking(state, booking.id, booking.date) : null
  );

  // Get tasks and notes separately to debug
  const tasks = useSelector((state: RootState) => selectTasksByJobId(state, jobId));
  const notes = useSelector((state: RootState) => selectNotesByJobId(state, jobId));


    const handleGoBack = () => {
    navigate({ to: '/job/$jobId/$date', params: { jobId, date } });
  };

  const handleUpdateJobState = () => {
    if (typeof jobState !== 'undefined') {
      dispatch(updateJobState({
        ...jobState,
        confirmed_tasks: moment().format('YYYY-MM-DD HH:mm:ss')
      }));
    } else {
      dispatch(addJobState({
        job_id: job.id,
        confirmed_tasks: moment().format('YYYY-MM-DD HH:mm:ss')
      }));
    }
    navigate({ to: '/job/$jobId/$date', params: { jobId, date } });
  };


  const showNotesModal = () => {
    dispatch(updateNotesModal({
      show: true,
      canAssignTask: true,
    }));
  };

  const showHelpModal = () => {
    dispatch(updateHelpModal(true));
  };

  const handleLoadMoreNotes = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    dispatch(loadMoreNotes({
      job_id: job.id,
      page: nextPage
    }));
  };

  if (!job) {
    return (
      <div className="col-span-2 flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Job not found</h2>
          <p className="text-gray-500">Could not load job data</p>
        </div>
      </div>
    );
  }


  return (
          <ScrollArea className="col-span-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-5rem)]">
            <div className="p-8  flex justify-center">
              <div className="max-w-4xl w-full space-y-8">

                <div className="space-y-4">
                  <h1 className="text-3xl font-bold leading-tight">
                    Please review all the tasks required to complete this job.
                  </h1>
                  <p className="text-base leading-relaxed my-2">
                    If the job description is not clear or you require further information, please call the office on 01376 519743
                  </p>
                </div>

                <div className="space-y-4">
                  <Label className="text-gray-800 font-semibold text-sm uppercase">
                    TASKS
                  </Label>
                  <JobTaskListView
                    canUpdateTimesheet={checkinToday && checkinToday.checked_in_at ? true : false}
                    canAddPhotos={checkinToday && checkinToday.checked_in_at ? true : false}
                    updateJobStatus={true}
                    checkin={checkinToday}
                    tasks={tasks}
                  />
                </div>

                <div className="space-y-4">
                  <Label className="text-gray-800 font-semibold text-sm uppercase">
                    NOTES
                  </Label>

                  <JobNotesList
                    notes={notes}
                    currentPage={currentPage}
                    onLoadMore={handleLoadMoreNotes}
                  />
                </div>

                <div className="flex gap-4 py-8 border-t border-gray-200">
                  <Button
                    onClick={jobState && jobState.confirmed_tasks && jobState.confirmed_tasks !== null ? handleGoBack : handleUpdateJobState}
                    className="flex-1"
                  >
                    {jobState && jobState.confirmed_tasks && jobState.confirmed_tasks !== null ? "Back to Overview" : "Proceed with Job"}
                  </Button>

                  <Button
                    variant="secondary"
                    onClick={showHelpModal}
                    className="flex-1"
                  >
                    I Need Assistance
                  </Button>
                </div>
              </div>
            </div>
</ScrollArea>  );
}
